import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, CheckCheck, RotateCcw, ExternalLink } from "lucide-react";
import AdminShell from "../components/AdminShell";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_OPTIONS = ["all", "new", "contacted", "archived"];

const StatusBadge = ({ status }) => {
  const styles =
    status === "new"
      ? "border-gray-300 text-white"
      : status === "contacted"
      ? "border-gray-600 text-gray-300"
      : "border-gray-800 text-gray-600";
  return (
    <span
      className={`inline-block px-3 py-1 border ${styles} text-[9px] font-display font-light uppercase tracking-[0.3em]`}
    >
      {status}
    </span>
  );
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("spinlab_token");
        window.location.href = "/admin";
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return leads;
    return leads.filter((l) => l.status === filter);
  }, [leads, filter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setOpen(null);
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const counts = useMemo(
    () => ({
      all: leads.length,
      new: leads.filter((l) => l.status === "new").length,
      contacted: leads.filter((l) => l.status === "contacted").length,
      archived: leads.filter((l) => l.status === "archived").length,
    }),
    [leads]
  );

  return (
    <AdminShell title="Lead Inbox" testId="admin-leads-page">
      <div className="flex items-center gap-2 flex-wrap mb-10">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            data-testid={`leads-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 border text-[10px] font-display font-light uppercase tracking-[0.3em] transition-colors ${
              filter === s
                ? "border-gray-300 text-white"
                : "border-gray-800 text-gray-500 hover:text-white hover:border-gray-600"
            }`}
          >
            {s} ({counts[s] || 0})
          </button>
        ))}
        <button
          data-testid="leads-refresh"
          onClick={load}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2 border border-gray-800 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-300 hover:text-white"
        >
          <RotateCcw strokeWidth={1} size={14} /> Refresh
        </button>
      </div>

      {loading ? (
        <p data-testid="leads-loading" className="text-xs font-display font-light uppercase tracking-[0.3em] text-gray-500">
          Loading…
        </p>
      ) : filtered.length === 0 ? (
        <div
          data-testid="leads-empty"
          className="border border-gray-900 p-16 text-center"
        >
          <p className="text-sm font-display font-light uppercase tracking-[0.3em] text-gray-500">
            No leads in this view
          </p>
        </div>
      ) : (
        <div data-testid="leads-table" className="border-t border-gray-900">
          <div className="hidden md:grid grid-cols-12 gap-4 py-4 px-2 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-600 border-b border-gray-900">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-3">Vehicle / Service</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Received</div>
          </div>
          {filtered.map((l) => (
            <React.Fragment key={l.id}>
              <button
                data-testid={`lead-row-${l.id}`}
                onClick={() => setOpen((o) => (o === l.id ? null : l.id))}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 py-5 px-2 border-b border-gray-900 hover:bg-[#080808] transition-colors"
              >
                <div className="md:col-span-3">
                  <div className="text-sm font-light text-white">{l.name}</div>
                  {l.email && <div className="text-xs font-light text-gray-500">{l.email}</div>}
                </div>
                <div className="md:col-span-2 text-sm text-gray-300">{l.phone}</div>
                <div className="md:col-span-3 text-sm text-gray-300">
                  <div>{l.year_make_model || l.vehicle_type || l.vehicle || "—"}</div>
                  {(l.service_needed || l.service_interest) && (
                    <div className="text-xs text-gray-500">{l.service_needed || l.service_interest}</div>
                  )}
                  {l.zip_code && (
                    <div className="text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-600 mt-1">ZIP {l.zip_code}</div>
                  )}
                </div>
                <div className="md:col-span-2"><StatusBadge status={l.status} /></div>
                <div className="md:col-span-2 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-500 md:text-right">
                  {new Date(l.created_at).toLocaleString()}
                </div>
              </button>
              {open === l.id && (
                <div
                  data-testid={`lead-detail-${l.id}`}
                  className="px-4 py-8 border-b border-gray-900 bg-[#050505]"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Vehicle</p>
                      <p className="text-gray-200">{l.year_make_model || l.vehicle || "—"}</p>
                      {l.vehicle_type && (
                        <p className="text-xs text-gray-500 mt-1">{l.vehicle_type}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Service Needed</p>
                      <p className="text-gray-200">{l.service_needed || l.service_interest || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Preferred Date</p>
                      <p className="text-gray-200">{l.preferred_date || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">ZIP / Location</p>
                      <p className="text-gray-200">{l.zip_code || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Condition</p>
                      <p className="text-gray-200">{l.vehicle_condition || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Pet Hair</p>
                      <p className="text-gray-200">{l.pet_hair || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Stains / Odors</p>
                      <p className="text-gray-200">{l.major_stains_odors || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Lead ID</p>
                      <p className="text-gray-500 text-xs font-mono break-all">{l.id}</p>
                    </div>
                    <div className="md:col-span-4">
                      <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-2">Message</p>
                      <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                        {l.message || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-8">
                    {l.email && (
                      <a
                        href={`mailto:${l.email}`}
                        data-testid={`lead-action-email-${l.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-200 hover:border-white hover:text-white transition-all"
                      >
                        <ExternalLink strokeWidth={1} size={14} /> Email
                      </a>
                    )}
                    <a
                      href={`sms:${l.phone}`}
                      data-testid={`lead-action-call-${l.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-200 hover:border-white hover:text-white transition-all"
                    >
                      <ExternalLink strokeWidth={1} size={14} /> Text
                    </a>
                    {l.status !== "contacted" && (
                      <button
                        data-testid={`lead-action-contacted-${l.id}`}
                        onClick={() => updateStatus(l.id, "contacted")}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-200 hover:border-white hover:text-white transition-all"
                      >
                        <CheckCheck strokeWidth={1} size={14} /> Mark Contacted
                      </button>
                    )}
                    {l.status !== "new" && (
                      <button
                        data-testid={`lead-action-new-${l.id}`}
                        onClick={() => updateStatus(l.id, "new")}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-200 hover:border-white hover:text-white transition-all"
                      >
                        <RotateCcw strokeWidth={1} size={14} /> Mark New
                      </button>
                    )}
                    {l.status !== "archived" && (
                      <button
                        data-testid={`lead-action-archive-${l.id}`}
                        onClick={() => updateStatus(l.id, "archived")}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-400 hover:border-gray-400 hover:text-white transition-all"
                      >
                        Archive
                      </button>
                    )}
                    <button
                      data-testid={`lead-action-delete-${l.id}`}
                      onClick={() => remove(l.id)}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-800 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-500 hover:border-gray-300 hover:text-white transition-all"
                    >
                      <Trash2 strokeWidth={1} size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminLeads;
